// Drop-in async replacements for window.confirm / window.prompt / window.alert
// that render styled modals instead of the OS-native popups.
//
// Why: Chrome's native dialogs are jarring inside an otherwise themed SPA —
// they don't match our colors, can't show formatted content, and on
// localhost they include the unhelpful "localhost:3000 says" prefix. They
// also block the JS thread, which interferes with React's render queue.
//
// Usage (identical signatures, except they return Promises):
//
//   import { confirm, prompt, alertBox } from '../lib/dialogs';
//
//   if (!(await confirm(`Delete "${name}"?`))) return;
//   const slug = await prompt('Type the slug to confirm:', name);
//   await alertBox('Done.');
//
// The provider must be mounted once at the app root (see DialogProvider).
// If it isn't, the helpers fall back to the native window.* APIs so the
// caller never crashes — handy during testing and during the migration
// from window.confirm to confirm().

let dialogHandler = null;

/** Internal: wired by <DialogProvider> on mount. */
export function _registerDialogHandler(fn) {
  dialogHandler = fn;
}

/**
 * Modal confirm. Returns a Promise<boolean>. Cancel/Escape/backdrop-click
 * all resolve to false; only the OK button resolves to true.
 *
 * Options:
 *   title         — heading at the top of the modal (default: "Confirm")
 *   confirmLabel  — text on the OK button (default: "OK")
 *   cancelLabel   — text on the Cancel button (default: "Cancel")
 *   danger        — when true, OK button uses the danger color (red)
 */
export function confirm(message, options = {}) {
  if (!dialogHandler) return Promise.resolve(window.confirm(message));
  return dialogHandler({ type: 'confirm', message, ...options });
}

/**
 * Modal prompt. Returns a Promise<string|null>. Cancel/Escape resolve to
 * null (matching window.prompt). OK resolves to the entered string,
 * including empty string if the field is left blank.
 *
 * Options:
 *   title         — heading
 *   confirmLabel  — OK button label
 *   placeholder   — input placeholder
 *   inputLabel    — label rendered above the input
 */
export function prompt(message, defaultValue = '', options = {}) {
  if (!dialogHandler) return Promise.resolve(window.prompt(message, defaultValue));
  return dialogHandler({ type: 'prompt', message, defaultValue, ...options });
}

/**
 * Modal alert. Returns a Promise<void> that resolves when dismissed.
 * Has only an OK button. Useful for "Action complete" notices that need
 * an explicit acknowledgement.
 */
export function alertBox(message, options = {}) {
  if (!dialogHandler) {
    window.alert(message);
    return Promise.resolve();
  }
  return dialogHandler({ type: 'alert', message, ...options });
}
